import type { OnChanges, OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import { DurationFormat, DurationPipe } from '@freelancer/pipes';
import type { Timer } from '@freelancer/time-utils';
import {
  FontColor,
  FontWeight,
  TextSize,
  TextVerticalAlign,
} from '@freelancer/ui/text';
import { textVariants } from '@freelancer/ui/text/tailwind.types';
import { LocalizedDateFns } from '../localized-date-fns.service';
import { RelativeTime } from './relative-time.service';
import { DAY, HOUR, MINUTE } from './relative-time.types';

@Component({
  standalone: true,
  imports: [FreelancerAsyncPipeModule],
  selector: 'fl-relative-time',
  template: `
    <span [class]="computedClass">
      @if (showJustNowString && !roundJustNowString) {
        <ng-container i18n="Recent relative date">just now</ng-container>
      } @else {
        <!-- Empty container is needed to preserve whitespace -->
        <ng-container>{{ relativeTime | flAsync }}</ng-container>
      }
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelativeTimeComponent implements OnInit, OnChanges, OnDestroy {
  private dateFns = inject(LocalizedDateFns);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private durationPipe = inject(DurationPipe);
  private relativeTimeService = inject(RelativeTime);

  computedClass: string;
  intervalId: Timer;
  relativeTime: Promise<string>;
  showJustNowString = false;

  @Input() set date(date: Date | string | number) {
    this._date = new Date(date);
    this.hasTimePassed = false;
  }

  _date: Date;
  private hasTimePassed: boolean;

  @Input() live = true;
  @Input() includeSeconds = true;
  @Input() suffix = true;
  @Input() strict = false;
  /** Display a secondary unit (eg. 1 hour, 1 minute). Can only display as if `strict` was on */
  @Input() secondaryUnit = false;
  /** Do not display the `just now` string even if `strict` mode is on - round to 1m instead */
  @Input() roundJustNowString = false;
  /** Allow time to tick down after it has passed */
  @Input() continueAfterTimePassed = true;
  /** Display the units in abbreviated form (eg. 2 seconds -> 2s) */
  @Input() shortFormat = false;
  /** Include a space between the number and the unit (eg. "2 s" instead of "2s"). Only applies for `shortFormat`. */
  @Input() includeSpace = false;
  @Input() roundingMethod?: 'ceil' | 'floor' | 'round';

  @Input() color: FontColor = FontColor.FOREGROUND;
  @Input() size?: TextSize = TextSize.BODY_16;
  @Input() sizeDesktop?: TextSize;
  @Input() sizeDesktopXXLarge?: TextSize;
  @Input() verticalAlign?: TextVerticalAlign;
  @Input() weight: FontWeight = FontWeight.NORMAL;

  @Output() timePassed = new EventEmitter<undefined>();

  tick = (): void => {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    const now = Date.now();
    this.render(now);
    if (!this.live) {
      return;
    }

    const then = this._date.getTime();
    const seconds = Math.round((then - now) / 1000);

    if (!this.hasTimePassed && seconds <= 0) {
      this.hasTimePassed = true;
      this.timePassed.emit();
      if (!this.continueAfterTimePassed) {
        return;
      }
    }

    // figure out how long to wait before recalculating
    // eg. if it says "4 hours" we know it won't be different in 1 minute, so we can wait longer.
    let period =
      Math.abs(seconds) < 2 * MINUTE
        ? 1000
        : seconds < HOUR
          ? 1000 * MINUTE
          : seconds < 2 * HOUR
            ? 1000 * 2 * MINUTE
            : seconds < 2 * DAY
              ? 1000 * HOUR
              : 1000 * DAY;

    if (this.secondaryUnit) {
      // if we show secondaryUnit, we want to check more often because we display more units
      period =
        Math.abs(seconds) < HOUR + MINUTE
          ? 1000
          : seconds < DAY + HOUR
            ? 1000 * MINUTE
            : 1000 * HOUR;
    }

    this.intervalId = this.relativeTimeService.scheduleTick(this.tick, period);
  };

  ngOnInit(): void {
    this.tick();
  }

  ngOnChanges(): void {
    this.tick();

    this.computedClass = textVariants({
      color: this.color,
      size: this.size,
      sizeDesktop: this.sizeDesktop,
      sizeDesktopXXL: this.sizeDesktopXXLarge,
      weight: this.weight,
      verticalAlign: this.verticalAlign,
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
  }

  private render(now: number): void {
    // show "just now" as a string because date-fns doesn't support these two options together
    this.showJustNowString =
      this.strict &&
      !this.includeSeconds &&
      now - this._date.getTime() < MINUTE * 1000;
    if (this.showJustNowString && !this.roundJustNowString) {
      return;
    }

    // otherwise use date-fns to format
    if (this.secondaryUnit) {
      const diff = Math.abs(now - this._date.getTime());
      this.relativeTime = this.durationPipe.transform(
        diff,
        DurationFormat.LONG,
      );
    } else if (
      this.strict &&
      // We want to round the just now string so let it go to the block using `formatDistance` instead
      (!this.showJustNowString || !this.roundJustNowString)
    ) {
      this.relativeTime = this.dateFns.formatDistanceStrict(
        this._date,
        new Date(),
        {
          addSuffix: this.suffix,
          shortFormat: this.shortFormat,
          roundingMethod: this.roundingMethod,
          includeSpace: this.includeSpace,
        },
      );
    } else {
      this.relativeTime = this.dateFns.formatDistance(this._date, new Date(), {
        includeSeconds: this.includeSeconds,
        addSuffix: this.suffix,
        shortFormat: this.shortFormat,
        includeSpace: this.includeSpace,
      });
    }
    this.changeDetectorRef.markForCheck();
  }
}
